import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AreaIcon } from '../components/AreaIcon';
import { Logo } from '../components/Logo';
import { useI18n } from '../i18n';
import { ABOUT } from './about-content';
import { AUTOMATIONS } from './about-automations';

/** "Discover Syndiqia": presentation of the product for prospective syndicates. Signed-in users only for now. */
export function AboutPage() {
  const { t, locale, setLocale } = useI18n();
  const c = ABOUT[locale];
  const d = AUTOMATIONS[locale];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [clipOpen, setClipOpen] = useState(false);

  return (
    <div className="about">
      <header className="topbar about-topbar">
        <Link to="/" aria-label={c.back}>
          <Logo />
        </Link>
        <div className="topbar-actions">
          <button type="button" className="link-button" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
            {t.language}
          </button>
          <Link to="/" className="link-button">
            {c.back}
          </Link>
        </div>
      </header>

      <section className="about-hero">
        <div className="about-wrap">
          <p className="about-eyebrow">{c.eyebrow}</p>
          <h1>{c.title}</h1>
          <p className="about-lead">{c.lead}</p>
          <div className="about-cta">
            <Link to="/" className="button button-primary">
              {c.openApp}
            </Link>
            <a href="#video" className="button">
              {c.video.cta}
            </a>
            <a href="#agents" className="button">
              {c.seeAgents}
            </a>
          </div>
          <ul className="about-points">
            {c.heroPoints.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="about-section about-section-alt" id="video">
        <div className="about-wrap">
          <h2>{c.video.title}</h2>
          <p className="about-section-lead">{c.video.lead}</p>
          {/* Self-hosted (no third-party player or tracking); metadata only until the visitor presses play. */}
          <video className="about-video" controls preload="metadata" playsInline poster="/video/syndiqia-presentation.jpg" aria-label={c.video.label}>
            <source src="/video/syndiqia-presentation.mp4" type="video/mp4" />
          </video>
        </div>
      </section>

      <section className="about-section">
        <div className="about-wrap">
          <h2>{c.problem.title}</h2>
          <div className="about-grid about-grid-3">
            {c.problem.items.map((i) => (
              <div key={i.title} className="about-card">
                <h3>{i.title}</h3>
                <p>{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-section-alt" id="agents">
        <div className="about-wrap">
          <h2>{c.agents.title}</h2>
          <p className="about-section-lead">{c.agents.lead}</p>
          <div className="about-grid about-grid-areas">
            {c.agents.areas.map((a) => (
              <article key={a.title} className={`about-card about-area${a.availability === 'soon' ? ' about-area-soon' : ''}`}>
                <header>
                  <AreaIcon name={a.icon} />
                  <h3>{a.title}</h3>
                  <span className={`badge ${a.availability === 'now' ? 'badge-status-resolved' : 'badge-draft'}`}>{c.agents.availability[a.availability]}</span>
                </header>
                <p className="about-label about-label-auto">{c.agents.automatedLabel}</p>
                <ul className="about-dots">
                  {a.automated.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
                <div className="about-human">
                  <p className="about-label">{c.agents.humanLabel}</p>
                  <p>{a.human}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="about-more">
            <a href="#automatisations">{d.link} →</a>
          </p>
        </div>
      </section>

      <section className="about-showcase">
        <div className="about-wrap about-showcase-inner">
          <div className="about-showcase-text">
            <p className="about-showcase-eyebrow">{c.showcase.eyebrow}</p>
            <h2>{c.showcase.title}</h2>
            <p>{c.showcase.text}</p>
            <div className="about-showcase-actions">
              <Link to="/about/automatisations" className="button about-showcase-button">
                {c.showcase.cta} →
              </Link>
              <button type="button" className="button about-showcase-secondary" onClick={() => setClipOpen(true)}>
                {c.showcase.video} →
              </button>
            </div>
          </div>
          <Link to="/about/automatisations" className="about-showcase-media" aria-label={c.showcase.cta}>
            {/* Silent loop recorded from the map page (apps/web/scripts/record-automation-map.mjs teaser); still image when motion is reduced. */}
            <video autoPlay={!reducedMotion} muted loop playsInline preload="metadata" poster="/video/carte-automatisations.jpg" aria-label={c.showcase.label}>
              <source src="/video/carte-automatisations.mp4" type="video/mp4" />
            </video>
          </Link>
        </div>
      </section>

      {clipOpen && <ClipPlayer label={c.showcase.videoLabel} close={c.showcase.close} onClose={() => setClipOpen(false)} />}

      <section className="about-section" id="automatisations">
        <div className="about-wrap">
          <h2>{d.title}</h2>
          <p className="about-section-lead">{d.lead}</p>
          <div className="about-autos">
            {d.items.map((x, i) => (
              <details key={x.title} className="about-auto" open={i === 0}>
                <summary>
                  <AreaIcon name={x.icon} />
                  <span className="about-auto-head">
                    <strong>{x.title}</strong>
                    <span className="about-auto-value">{x.value}</span>
                  </span>
                  <span className={`badge about-mode about-mode-${x.mode}`}>{d.modes[x.mode]}</span>
                </summary>
                <div className="about-auto-body">
                  <div className="about-auto-before">
                    <p className="about-label">{d.labels.before}</p>
                    <p>{x.before}</p>
                  </div>
                  <div>
                    <p className="about-label">{d.labels.trigger}</p>
                    <p>{x.trigger}</p>
                    <p className="about-label about-label-auto">{d.labels.steps}</p>
                    <ol className="about-auto-steps">
                      {x.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="about-auto-modes">
                    {x.confirm && (
                      <div className="about-human">
                        <p className="about-label">{d.labels.confirm}</p>
                        <p>{x.confirm}</p>
                      </div>
                    )}
                    {x.auto && (
                      <div className="about-auto-on">
                        <p className="about-label about-label-auto">{x.mode === 'always' ? d.labels.always : d.labels.auto}</p>
                        <p>{x.auto}</p>
                      </div>
                    )}
                    <p className="about-label">{d.labels.guardrails}</p>
                    <ul className="about-checks">
                      {x.guardrails.map((g) => (
                        <li key={g}>{g}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </details>
            ))}
          </div>
          <p className="muted small about-note">{d.note}</p>
        </div>
      </section>

      <section className="about-section about-section-alt">
        <div className="about-wrap">
          <h2>{c.flow.title}</h2>
          <p className="about-section-lead">{c.flow.lead}</p>
          <ol className="about-flow">
            {c.flow.steps.map((s, i) => (
              <li key={s.title} className={i === 2 ? 'about-flow-human' : undefined}>
                <span className="about-step">{i + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="about-section">
        <div className="about-wrap about-humans">
          <div>
            <h2>{c.humans.title}</h2>
            <p className="about-section-lead">{c.humans.lead}</p>
            <ul className="about-human-list">
              {c.humans.items.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
          <aside className="about-card about-approval">
            <h3>{c.humans.approvalTitle}</h3>
            <ul>
              {c.humans.approvalItems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="about-section about-section-alt">
        <div className="about-wrap">
          <h2>{c.trust.title}</h2>
          <div className="about-grid about-grid-3">
            {c.trust.items.map((i) => (
              <div key={i.title} className="about-trust">
                <h3>{i.title}</h3>
                <p>{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="about-wrap">
          <h2>{c.offers.title}</h2>
          <p className="about-section-lead">{c.offers.lead}</p>
          <div className="about-grid about-grid-3">
            {c.offers.plans.map((p) => (
              <div key={p.name} className={`about-card about-plan${p.highlight ? ' about-plan-highlight' : ''}`}>
                <h3>{p.name}</h3>
                <p className="muted">{p.audience}</p>
                <p className="about-price">{p.price}</p>
                <p className="muted small">{p.priceNote}</p>
                <ul className="about-checks">
                  {p.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <ul className="about-checks about-terms">
            {c.offers.terms.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          <p className="muted small about-note">{c.offers.note}</p>
        </div>
      </section>

      <section className="about-section about-section-alt">
        <div className="about-wrap about-faq">
          <h2>{c.faq.title}</h2>
          {c.faq.items.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="about-closing">
        <div className="about-wrap">
          <h2>{c.closing.title}</h2>
          <p>{c.closing.text}</p>
          <Link to="/" className="button about-closing-button">
            {c.openApp}
          </Link>
        </div>
      </section>

      <footer className="about-footer">
        <p className="muted small">{c.confidential}</p>
      </footer>
    </div>
  );
}

/** The one-minute clip of the automation map, played over the page (Escape or a click outside closes it). */
function ClipPlayer({ label, close, onClose }: { label: string; close: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="about-lightbox" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="about-lightbox-frame" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="about-lightbox-close" onClick={onClose} aria-label={close} autoFocus>
          ×
        </button>
        <video controls autoPlay playsInline poster="/video/carte-automatisations-clip.jpg">
          <source src="/video/carte-automatisations-clip.mp4" type="video/mp4" />
        </video>
      </div>
    </div>
  );
}
